import { forwardRef } from 'react';

import type { FieldConfidenceResponse } from '../api';
import type { FieldEditState } from '../hooks/useFieldEdit';
import { hasIssue } from '../hooks/useFieldAggregates';
import { AccordionFieldShell } from './AccordionFieldShell';
import { ConfidenceMeter } from './ConfidenceMeter';
import { FieldRow } from './FieldRow';

interface GroupFieldRowProps {
  group: FieldConfidenceResponse;
  edits: Record<string, FieldEditState>;
  highlightedFieldName: string | null;
  onCommit: (fieldName: string, value: unknown) => void;
  onFocus: (fieldName: string) => void;
}

/**
 * Renders a Group field as an accordion via `AccordionFieldShell`. The header carries the
 * group's aggregate confidence (mean of children scores, worst-status badge); the body
 * lists each child as a standard `FieldRow`. Auto-opens when at least one descendant
 * needs review (the same `hasIssue` heuristic shared with `TableFieldRow`).
 *
 * Child editing: the FieldRow's onCommit propagates the qualified name (e.g.
 * `cliente.razon_social`) which the API endpoint forwards to PatchDocumentFieldUseCase.
 */
export const GroupFieldRow = forwardRef<HTMLDivElement, GroupFieldRowProps>(function GroupFieldRow(
  { group, edits, highlightedFieldName, onCommit, onFocus },
  ref,
): JSX.Element {
  const children = group.children ?? [];
  const childrenNeedingReview = children.filter((c) => hasIssue(c, edits)).length;
  const defaultOpen = childrenNeedingReview > 0;

  return (
    <AccordionFieldShell
      ref={ref}
      fieldName={group.name}
      title={group.name}
      subtitle={
        <>
          {children.length} sub-campos
          {childrenNeedingReview > 0 ? (
            <span className="ml-2 text-amber-700">
              · {childrenNeedingReview} requieren atención
            </span>
          ) : null}
        </>
      }
      leading={<ConfidenceMeter score={group.score} status={group.status} />}
      badge={
        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-800 ring-1 ring-indigo-200">
          Grupo
        </span>
      }
      meta={
        group.issue !== undefined && group.issue !== '' ? (
          <p className="text-xs text-amber-700">{group.issue}</p>
        ) : undefined
      }
      defaultOpen={defaultOpen}
      highlighted={highlightedFieldName === group.name}
    >
      <div className="space-y-3">
        {children.map((child) => {
          const valueIsMissing =
            child.value === null || child.value === undefined || child.value === '';
          return (
            <FieldRow
              key={child.name}
              field={child}
              edit={edits[child.name]}
              missingRequired={valueIsMissing && child.status === 'critical'}
              highlighted={highlightedFieldName === child.name}
              onCommit={onCommit}
              onFocus={onFocus}
            />
          );
        })}
      </div>
    </AccordionFieldShell>
  );
});
